import React, { useEffect, useState } from "react";
import { DefaultButton, PrimaryButton } from "@fluentui/react";
import { Flowstep, Operation } from "shared/types/flowstep";
import { OfficeJsWorkbookApi } from "@v2/services/workbook-api/office-js-workbook-api";
import { RangeType } from "shared/utils/flowstep";
import { useStyles } from './ExecutionConfirmationDialog.styles';
import { capitalizeString } from "shared/utils/utils";
import { plurifySting } from "shared/utils/string-utils";
import { getRowIndexesToRead, stringToRowIndexes } from "shared/execution/executors/common";

interface ExecutionConfirmationDialogProps {
  onResult: (result: boolean) => void;
  flowstep: Flowstep;
}

export const ExecutionConfirmationDialog: React.FC<ExecutionConfirmationDialogProps> = (props) => {
  const [ rowCount, setRowCount ] = useState(0);
  const [ isReady, setReady ] = useState(false);
  const { flowstep, onResult } = props;
  const { provider } = flowstep;

  const styles = useStyles();

  const calcAffectedRowCount = async () => {
    const { rangeToApply, worksheetId, target, onlyVisibleRows } = flowstep.options;

    if (!rangeToApply) {
      return 0;
    }

    return await Excel.run(async (context) => {

      const workbookApi = new OfficeJsWorkbookApi(context);
      const tableRange = await workbookApi.detectRangeByCell(worksheetId, target);
      if (!tableRange) {
        return 0;
      }

      const options = {
        visibleOnly: onlyVisibleRows,
        selectedOnly: rangeToApply.type === RangeType.Selected,
        specifiedRowNumbers: await getRowIndexesToRead(flowstep, tableRange, workbookApi),
      };

      return await workbookApi.calcRowCountToRead(tableRange, options);
    });
  }

  useEffect(() => {
    (async () => {
      try {
        const rowCount = await calcAffectedRowCount();
        setRowCount(rowCount);
      } catch(error: any) {
        console.error('Could not count the number of records: ', error.message);
      } finally {
        setReady(true);
      }
    })()
  }, []);

  const getActionName = () => {
    switch (flowstep.operation) {
      case Operation.INSERT: return 'inserted';
      case Operation.UPDATE: return 'updated';
      case Operation.DELETE: return 'deleted';
      default: return 'affected';
    }
  }

  return (<>
    <div className={styles.executionConfirmation}>
      <div className={styles.header}>Confirmation</div>
      <div className={styles.content}>
        {isReady && rowCount > 0 && (<>
          { rowCount } {plurifySting('record', rowCount)} will
          be { getActionName() } in { capitalizeString(provider) } as the result of this action. Continue?
        </>)}
        {isReady && rowCount === 0 && (
          <div>
            Could not count the number of records that will be { getActionName() }.
          </div>
        )}
        {!isReady && (<div>Counting...</div>)}
      </div>
      <div className={styles.buttonPanel}>
        {(!isReady || rowCount > 0) && (<>
          <PrimaryButton disabled={!isReady} onClick={() => onResult(true)}>Yes</PrimaryButton>
          <DefaultButton disabled={!isReady} onClick={() => onResult(false)}>No</DefaultButton>
        </>)}
        {isReady && rowCount === 0 && (<>
          <DefaultButton onClick={() => onResult(true)}>Continue</DefaultButton>
          <DefaultButton disabled={!isReady} onClick={() => onResult(false)}>Cancel</DefaultButton>
        </>)}
      </div>
    </div>
  </>)
}
